import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { 
  Send, CheckCircle, Loader2, AlertCircle, 
  Wine, Package, Truck, Users, ArrowRight,
  Building2, Mail, Phone, User, FileText,
  Target, Clock, DollarSign, Box
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StepCard({ number, title, text }) {
  return (
    <div className="relative bg-white rounded-2xl border border-slate-200 p-6 hover:border-copper/30 transition-colors">
      <div className="absolute -top-4 left-6 w-8 h-8 rounded-full bg-copper text-white flex items-center justify-center font-bold text-sm">
        {number}
      </div>
      <h3 className="font-semibold text-navy mt-2 mb-2">{title}</h3>
      <p className="text-slate-600 text-sm leading-relaxed">{text}</p>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, text }) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
      <div className="w-10 h-10 rounded-lg bg-copper/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-copper" />
      </div>
      <div>
        <h4 className="font-semibold text-navy text-sm">{title}</h4>
        <p className="text-slate-600 text-sm mt-1">{text}</p>
      </div>
    </div>
  );
}

export default function BuyerMatch() {
  const { user, isAuthenticated } = useAuth();
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    product_category: '',
    product_type: '',
    estimated_volume: '',
    target_market: '',
    timeline: '',
    packaging_needed: '',
    budget_range: '',
    notes: ''
  });

  useEffect(() => {
    fetchOptions();
  }, []);

  // Auto-fill if user is logged in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
        company: user.company || prev.company,
        phone: user.phone || prev.phone
      }));
    }
  }, [user]);

  const fetchOptions = async () => {
    try {
      const res = await fetch(`${API}/buyer-matches/options`);
      if (res.ok) {
        const data = await res.json();
        setOptions(data);
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        user_id: user?.id || null
      };

      const res = await fetch(`${API}/buyer-matches/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to submit request');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Success State
  if (submitted) {
    return (
      <Layout>
        <div className="min-h-[80vh] bg-slate-50 flex items-center justify-center px-4">
          <div className="max-w-lg w-full bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-navy mb-3">
              Request Received!
            </h1>
            <p className="text-slate-600 mb-6">
              We've received your match request and will review it within 1-2 business days. 
              You'll hear from us at <strong>{formData.email}</strong>.
            </p>
            
            {!isAuthenticated && (
              <div className="bg-copper/5 border border-copper/20 rounded-xl p-4 mb-6">
                <p className="text-sm text-navy mb-3">
                  <strong>Want to track your request?</strong> Create a free account to see match status and connect with suppliers.
                </p>
                <Link
                  to="/register"
                  className="btn-copper inline-flex items-center gap-2"
                  data-testid="create-account-btn"
                >
                  Create Free Account
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <Link
                to="/"
                className="px-5 py-2.5 border border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors"
              >
                Back to Home
              </Link>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    name: user?.name || '',
                    email: user?.email || '',
                    company: user?.company || '',
                    phone: user?.phone || '',
                    product_category: '',
                    product_type: '',
                    estimated_volume: '',
                    target_market: '',
                    timeline: '',
                    packaging_needed: '',
                    budget_range: '',
                    notes: ''
                  });
                }}
                className="px-5 py-2.5 bg-navy text-white rounded-xl font-medium hover:bg-navy/90 transition-colors"
              >
                Submit Another
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-navy text-white py-16 lg:py-20">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded-full bg-copper/20 text-copper text-sm font-medium mb-4">
              Beverage Sourcing & Matching
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Tell us what you need. We'll match you with the right partner.
            </h1>
            <p className="text-lg text-white/80 mb-6">
              BevRelay connects brands, buyers, and operators with qualified suppliers for 
              spirits, wine, RTDs, water, packaging, co-packing, and distribution opportunities.
            </p>
            <div className="flex flex-wrap gap-6 text-sm">
              {[
                { icon: Wine, text: "Spirits, Wine, RTD, Water" },
                { icon: Package, text: "Co-pack & Private Label" },
                { icon: Truck, text: "Distribution & Fulfillment" }
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-white/70">
                  <Icon className="w-4 h-4 text-copper" />
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="bg-slate-50 py-12 lg:py-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-10">
            
            {/* Form Section - 3 columns */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm" id="buyer-form">
                <div className="mb-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-copper">Buyer Intake</span>
                  <h2 className="font-heading text-2xl font-bold text-navy mt-1">Request a Match</h2>
                  <p className="text-slate-600 text-sm mt-2">
                    Share your needs and we'll connect you with qualified suppliers. No commitment required.
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 mb-6">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Contact Information */}
                  <div>
                    <h3 className="font-semibold text-navy text-sm uppercase tracking-wide mb-4 flex items-center gap-2">
                      <User className="w-4 h-4 text-copper" />
                      Contact Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-navy mb-1">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          required
                          className="input-field"
                          placeholder="John Smith"
                          data-testid="match-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy mb-1">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          required
                          className="input-field"
                          placeholder="john@company.com"
                          data-testid="match-email-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy mb-1">
                          Company <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.company}
                          onChange={(e) => handleChange('company', e.target.value)}
                          required
                          className="input-field"
                          placeholder="Your Company"
                          data-testid="match-company-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy mb-1">Phone</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          className="input-field"
                          placeholder="(555) 123-4567"
                          data-testid="match-phone-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Product Details */}
                  <div>
                    <h3 className="font-semibold text-navy text-sm uppercase tracking-wide mb-4 flex items-center gap-2">
                      <Box className="w-4 h-4 text-copper" />
                      Product Details
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-navy mb-1">
                          Product Category <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.product_category}
                          onChange={(e) => handleChange('product_category', e.target.value)}
                          required
                          className="input-field"
                          data-testid="match-category-select"
                        >
                          <option value="">Select category...</option>
                          {options?.product_categories?.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy mb-1">Product Type</label>
                        <select
                          value={formData.product_type}
                          onChange={(e) => handleChange('product_type', e.target.value)}
                          className="input-field"
                          data-testid="match-type-select"
                        >
                          <option value="">Select type...</option>
                          {options?.product_types?.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy mb-1">
                          Estimated Volume <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.estimated_volume}
                          onChange={(e) => handleChange('estimated_volume', e.target.value)}
                          required
                          className="input-field"
                          data-testid="match-volume-select"
                        >
                          <option value="">Select volume...</option>
                          {options?.volume_ranges?.map(vol => (
                            <option key={vol} value={vol}>{vol}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy mb-1">Packaging Needed</label>
                        <select
                          value={formData.packaging_needed}
                          onChange={(e) => handleChange('packaging_needed', e.target.value)}
                          className="input-field"
                          data-testid="match-packaging-select"
                        >
                          <option value="">Select option...</option>
                          {options?.packaging_needs?.map(pkg => (
                            <option key={pkg} value={pkg}>{pkg}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Market & Timeline */}
                  <div>
                    <h3 className="font-semibold text-navy text-sm uppercase tracking-wide mb-4 flex items-center gap-2">
                      <Target className="w-4 h-4 text-copper" />
                      Market & Timeline
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-navy mb-1">
                          Target Market <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.target_market}
                          onChange={(e) => handleChange('target_market', e.target.value)}
                          required
                          className="input-field"
                          data-testid="match-market-select"
                        >
                          <option value="">Select market...</option>
                          {options?.target_markets?.map(market => (
                            <option key={market} value={market}>{market}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy mb-1">
                          Timeline <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleChange('timeline', e.target.value)}
                          required
                          className="input-field"
                          data-testid="match-timeline-select"
                        >
                          <option value="">Select timeline...</option>
                          {options?.timelines?.map(tl => (
                            <option key={tl} value={tl}>{tl}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy mb-1">Budget Range</label>
                        <select
                          value={formData.budget_range}
                          onChange={(e) => handleChange('budget_range', e.target.value)}
                          className="input-field"
                          data-testid="match-budget-select"
                        >
                          <option value="">Select range...</option>
                          {options?.budget_ranges?.map(budget => (
                            <option key={budget} value={budget}>{budget}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1">
                      <FileText className="w-4 h-4 inline mr-1 text-copper" />
                      Additional Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      rows={3}
                      className="input-field resize-none"
                      placeholder="Tell us more about what you're looking for, specific requirements, or any questions..."
                      data-testid="match-notes-input"
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting || loading}
                    className="w-full btn-copper py-4 text-base flex items-center justify-center gap-2"
                    data-testid="match-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Submit Match Request
                      </>
                    )}
                  </button>

                  <p className="text-xs text-slate-500 text-center">
                    By submitting, you agree to be contacted about relevant opportunities. 
                    No spam, ever.
                  </p>
                </form>
              </div>
            </div>

            {/* Sidebar - 2 columns */}
            <div className="lg:col-span-2 space-y-6">
              {/* How It Works */}
              <div>
                <h3 className="font-heading text-lg font-bold text-navy mb-4" id="how-it-works">
                  How It Works
                </h3>
                <div className="space-y-6">
                  <StepCard 
                    number={1}
                    title="Tell us what you need"
                    text="Share your product type, market, packaging, and estimated volume so we understand the opportunity."
                  />
                  <StepCard 
                    number={2}
                    title="We review and match"
                    text="BevRelay filters for supplier fit, capabilities, and commercial relevance before connecting parties."
                  />
                  <StepCard 
                    number={3}
                    title="Move into real conversations"
                    text="Qualified matches move into direct discussions for pricing, production, or partnership."
                  />
                </div>
              </div>

              {/* Why BevRelay */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="font-heading text-lg font-bold text-navy mb-4">
                  Why Use BevRelay?
                </h3>
                <div className="space-y-3">
                  <FeatureCard 
                    icon={Users}
                    title="Qualified Introductions"
                    text="Not random lists — curated matches based on real fit."
                  />
                  <FeatureCard 
                    icon={Target}
                    title="Category Expertise"
                    text="We know spirits, wine, RTD, water, and beverage supply chains."
                  />
                  <FeatureCard 
                    icon={Clock}
                    title="Faster Path to Partners"
                    text="Skip the cold outreach — get warm introductions."
                  />
                </div>
              </div>

              {/* For Suppliers CTA */}
              <div className="bg-navy rounded-2xl p-6 text-white" id="suppliers">
                <h3 className="font-heading text-lg font-bold mb-2">
                  Are You a Supplier?
                </h3>
                <p className="text-white/80 text-sm mb-4">
                  Get access to qualified buyer inquiries and grow your business with serious opportunities.
                </p>
                <Link
                  to="/suppliers"
                  className="inline-flex items-center gap-2 bg-copper hover:bg-copper-dark text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Learn About Supplier Access
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
